import { useCallback, useState } from 'react';
import { useUserSettings } from '../contexts/UserSettingsContext';
import {
  dismissAutoApplyOffer,
  isAutoApplyOfferDismissed,
} from '../utils/replyCaptureStorage';
import { AutoApplyOfferSurface, UserSettings } from '../types';

interface AutoApplyOffer {
  /**
   * True when this surface should render the card. Callers still AND this with
   * their own earned-the-offer condition (enough opportunities / enough
   * applications), which is the only part that differs between hosts.
   */
  visible: boolean;
  /**
   * Non-null exactly when `visible` is true, so hosts can hand it straight to
   * the card without a redundant null check. The card's modal PUTs all five
   * Auto-Apply fields, so it must never be seeded from a placeholder.
   */
  settings: UserSettings | null;
  onSaved: (settings: UserSettings) => void;
  dismiss: () => void;
}

/**
 * Shared visibility rules for the Auto-Apply offer card. Everything a host
 * would otherwise reimplement lives here: settings loaded, Auto-Apply actually
 * off, and per-surface dismissal (dismissing on one page leaves the other free
 * to ask once).
 */
export const useAutoApplyOffer = (surface: AutoApplyOfferSurface): AutoApplyOffer => {
  const { settings, applySettings } = useUserSettings();
  const [dismissed, setDismissed] = useState(() => isAutoApplyOfferDismissed(surface));

  const dismiss = useCallback(() => {
    dismissAutoApplyOffer(surface);
    setDismissed(true);
  }, [surface]);

  // `=== false` rather than a falsy check: null settings mean "we don't know",
  // which must not read as "Auto-Apply is off".
  const visible = !dismissed && settings?.autoApplyEnabled === false;

  return {
    visible,
    settings: visible ? settings : null,
    onSaved: applySettings,
    dismiss,
  };
};
